import { useState, useMemo, useEffect } from 'react'
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { DateRange } from 'react-day-picker'
import { Calendar as CalendarIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Calendar } from '@/components/ui/Calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'

export interface DateRangeFilterProps {
  value?: DateRange
  onChange: (value: DateRange | undefined) => void
}

export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<DateRange | undefined>(value)

  useEffect(() => {
    if (open) {
      setDraft(value)
    }
  }, [open, value])

  useEffect(() => {
    if (!open) {
      setDraft(value)
    }
  }, [value, open])

  const label = useMemo(() => {
    if (!value?.from) return 'Selecione o período'
    if (!value.to) return format(value.from, "dd 'de' MMM, yyyy", { locale: ptBR })
    return `${format(value.from, "dd 'de' MMM", { locale: ptBR })} - ${format(value.to, "dd 'de' MMM, yyyy", { locale: ptBR })}`
  }, [value?.from, value?.to])

  const presets = useMemo(() => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    return [
      { label: 'Hoje', range: { from: today, to: today } },
      { label: 'Ontem', range: { from: subDays(today, 1), to: subDays(today, 1) } },
      { label: 'Últimos 7 dias', range: { from: subDays(today, 6), to: today } },
      { label: 'Últimos 14 dias', range: { from: subDays(today, 13), to: today } },
      { label: 'Últimos 30 dias', range: { from: subDays(today, 29), to: today } },
      { label: 'Este mês', range: { from: startOfMonth(today), to: endOfMonth(today) } },
      { label: 'Mês passado', range: { from: startOfMonth(subMonths(today, 1)), to: endOfMonth(subMonths(today, 1)) } },
    ]
  }, [])

  const handleApply = () => {
    onChange(draft)
    setOpen(false)
  }

  const handleClear = () => {
    setDraft(undefined)
    onChange(undefined)
    setOpen(false)
  }

  const isActive = (presetRange: { from: Date; to: Date }) => {
    if (!draft?.from || !draft.to) return false
    return (
      draft.from.toDateString() === presetRange.from.toDateString() &&
      draft.to.toDateString() === presetRange.to.toDateString()
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-[280px] justify-start text-left font-normal',
            !value && 'text-muted-foreground'
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto overflow-hidden rounded-xl p-0">
        <div className="flex">
          {/* Presets - Desktop */}
          <div className="hidden w-40 space-y-1 border-r border-border p-3 sm:block">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Atalhos</p>
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => setDraft(preset.range)}
                className={cn(
                  'w-full rounded-md border border-transparent px-2 py-1.5 text-left text-xs transition-colors',
                  isActive(preset.range)
                    ? 'bg-primary/20 text-primary border-primary/30'
                    : 'hover:bg-muted'
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="p-3">
            {/* Presets - Mobile */}
            <div className="mb-2 flex flex-wrap items-center gap-1 sm:hidden">
              {presets.slice(0, 4).map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => setDraft(preset.range)}
                  className={cn(
                    'rounded-md px-2 py-1 text-[10px] transition-colors',
                    isActive(preset.range)
                      ? 'bg-primary/20 text-primary'
                      : 'hover:bg-muted text-muted-foreground'
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <Calendar
              mode="range"
              selected={draft}
              onSelect={setDraft}
              numberOfMonths={2}
              defaultMonth={draft?.from}
            />

            <div className="mt-3 flex gap-2">
              <Button
                type="button"
                variant="ghost"
                className="flex-1"
                onClick={handleClear}
                disabled={!draft?.from && !draft?.to}
              >
                Limpar
              </Button>
              <Button type="button" className="flex-1" onClick={handleApply}>
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
